import { Dispatch, FC, SetStateAction, useContext, useEffect, useMemo, useState } from 'react'
import { Box, Button, Checkbox, TextField, Typography } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import dayjs, { Dayjs } from 'dayjs'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import ConfirmationModal from '../../Modals/ConfirmationModal'
import { IRequest } from '../../../../types/exercise.type'
import { IClassificationType } from '../../../../types/classifications.type'
import { RequestContext } from '../../../../contexts/request.context'
import { ITier, ITierCommand } from '../../../../../shared/types/api/tier.type'
import { updateRequest, deleteRequest, getAllClassificationTypes } from '../../../../services/request-form.service'
import { getAllTiers } from '../../../../services/tier.service'
import { useToast } from '../../../../contexts/toast.context'
import { SelectWithSearch } from '../../../shared/SearchSelect'

import styles from './styles.module.scss'

interface IProps {
  setValue: Dispatch<SetStateAction<number>>
  selectedRequest: IRequest
}

const LEVEL_RANK: Record<string, number> = {
  f207: 6,
  f206: 5,
  f205: 4,
  f204: 3,
  f203: 2,
  f202: 1,
  f201: 0,
  f200: -1,
}

// Find the best parent tier to use as the "Manager" for a given f207 tier.
// If there are multiple parents, prefer the one lowest in the org hierarchy
// (e.g. f205 over f200), since that is the most specific commanding tier.
// However, skip f201/f202 unless there is no other parent, because those
// are administrative levels that don't represent a real manager.
// If there are still ties, break by lowest id.
function findManager(tierId: number, tiers: ITier[], edges: ITierCommand[]): ITier | null {
  const tierById = new Map(tiers.map((t) => [t.id, t]))
  const parents = edges
    .filter((e) => e.commanded_tier_id === tierId)
    .map((e) => tierById.get(e.commanding_tier_id)!)
    .filter(Boolean)

  if (parents.length === 0) return null

  parents.sort((a, b) => {
    const aSkip = a.level === 'f201' || a.level === 'f202'
    const bSkip = b.level === 'f201' || b.level === 'f202'
    if (aSkip !== bSkip) return aSkip ? 1 : -1

    const aRank = LEVEL_RANK[a.level] ?? -2
    const bRank = LEVEL_RANK[b.level] ?? -2
    if (aRank !== bRank) return bRank - aRank

    return a.id - b.id
  })

  return parents[0]
}

const GeneralSection: FC<IProps> = ({ setValue, selectedRequest }) => {
  const { exercises, setRequests, setSelectedRequest } = useContext(RequestContext)

  const [request, setRequest] = useState<IRequest | null>(null)
  const [isDelete, setIsDelete] = useState(false)
  const [isCustomDate, setIsCustomDate] = useState(false)
  const [tiers, setTiers] = useState<ITier[]>([])
  const [edges, setEdges] = useState<ITierCommand[]>([])
  const [classificationTypes, setClassificationTypes] = useState<IClassificationType[]>([])

  const { showToast } = useToast()

  const f207Tiers = useMemo(() => tiers.filter((t) => t.level === 'f207'), [tiers])
  const f910Tiers = useMemo(() => tiers.filter((t) => t.level === 'f910'), [tiers])

  const selectedUnit = useMemo(
    () => f207Tiers.find((t) => t.id === request?.tiers_by_level?.f207) ?? null,
    [f207Tiers, request?.tiers_by_level],
  )

  const selectedSubAccount = useMemo(
    () => f910Tiers.find((t) => t.id === request?.tiers_by_level?.f910) ?? null,
    [f910Tiers, request?.tiers_by_level],
  )

  const manager = useMemo(() => {
    if (!selectedUnit) return ''
    const parent = findManager(selectedUnit.id, tiers, edges)
    return parent?.name ?? ''
  }, [selectedUnit, tiers, edges])

  const handleClose = () => {
    setIsDelete(false)
  }

  const handleSave = async (shouldNavigate?: boolean) => {
    if (request && Object.keys(request.tiers_by_level ?? {}).length > 0) {
      const data = await updateRequest(selectedRequest.id, request)

      if (data.success && data.data) {
        const parsedRequest = {
          ...data.data,
          start_date: dayjs(data.data.start_date).utc(true),
          end_date: dayjs(data.data.end_date).utc(true),
        }

        setRequests((prev) => prev.map((elem) => (elem.id === selectedRequest.id && data.data ? parsedRequest : elem)))
        setSelectedRequest(parsedRequest)
        handleClose()
        showToast('success', 'Request successfully updated')

        if (shouldNavigate) {
          setValue(1)
        }
      } else {
        showToast('error', 'Something went wrong, please try again')
      }
    } else {
      showToast('error', 'Please fill out all required fields')
    }
  }

  const handleDelete = async () => {
    const data = await deleteRequest(selectedRequest.id)

    if (data.success) {
      setRequests((prev) => prev.filter((elem) => elem.id !== selectedRequest.id))
      setSelectedRequest(null)
      showToast('success', data.message)
      handleClose()
    }
  }

  const handleChange = (key: string, value?: string | number | Dayjs) => {
    if (request) {
      setRequest({ ...request, [key]: value })
    }
  }

  const handleTierChange = (level: string, tierId: number | undefined) => {
    if (!request) return
    const updated = { ...request.tiers_by_level }
    if (tierId) {
      updated[level] = tierId
    } else {
      delete updated[level]
    }
    setRequest({ ...request, tiers_by_level: updated })
  }

  const handleNumericChange = (attr: string, value: string) => {
    if (/^\d*$/.test(value)) {
      handleChange(attr, value)
    }
  }

  const getClassificationTypes = async () => {
    const data = await getAllClassificationTypes()

    if (data.success && data.data) {
      setClassificationTypes(data.data)
    }
  }

  useEffect(() => {
    setRequest(selectedRequest)

    const isCustomDate = selectedRequest.start_date?.isValid() && selectedRequest.end_date?.isValid()

    setIsCustomDate(!!isCustomDate)
  }, [selectedRequest])

  useEffect(() => {
    const fetchTiers = async () => {
      const data = await getAllTiers()
      if (data.success && data.data) {
        setTiers(data.data.tiers)
        setEdges(data.data.edges)
      }
    }
    fetchTiers()
    getClassificationTypes()
  }, [])

  return (
    <Box className={styles.generalSection}>
      <Box className={styles.actions}>
        <Button className={styles.saveBtn} variant="outlined" onClick={() => handleSave()}>
          Save
        </Button>
        <Button
          className={styles.deleteBtn}
          variant="outlined"
          data-testid="deleteButton"
          onClick={() => setIsDelete(true)}
        >
          Delete
        </Button>
      </Box>
      <Box className={styles.sectionContainer}>
        <Box className={styles.titleContainer}>
          <Typography className={styles.title}>Requestor</Typography>
        </Box>
        <Box className={styles.section}>
          <Box className={styles.fieldContainer}>
            <Typography className={styles.label}>
              Name<span className={styles.asterisk}>*</span>:
            </Typography>
            <TextField
              value={request?.username || ''}
              onChange={(evt) => handleChange('username', evt.target.value)}
              className={styles.field}
              placeholder="Enter Value"
            />
          </Box>
          <Box className={styles.fieldContainer}>
            <Typography className={styles.label}>
              Phone<span className={styles.asterisk}>*</span>:
            </Typography>
            <TextField
              id="phoneNumber"
              className={styles.field}
              value={request?.user_phone || ''}
              inputMode="numeric"
              type="text"
              onChange={(evt) => handleNumericChange('user_phone', evt.target.value)}
              placeholder="Enter Value"
            />
          </Box>
        </Box>
      </Box>
      <Box className={styles.sectionContainer}>
        <Box className={styles.titleContainer}>
          <Typography className={styles.title}>Request For</Typography>
        </Box>
        <Box className={styles.section}>
          <Box className={styles.fieldContainer}>
            <Typography className={styles.label}>
              Exercise<span className={styles.asterisk}>*</span>:
            </Typography>
            <SelectWithSearch
              width={300}
              value={exercises
                .map((exercise) => ({
                  label: exercise.name,
                  value: exercise.id,
                }))
                .find((elem) => elem.value === request?.exercise_id)}
              label="Exercise"
              handleChange={(value) => handleChange('exercise_id', Number(value))}
              options={exercises.map((exercise) => ({
                value: exercise.id,
                label: exercise.name,
              }))}
            />
          </Box>
          <Box>
            <Box className={styles.checkboxContainer}>
              <Typography className={styles.label}>Add custom date range</Typography>
              <Checkbox
                data-testid="custom-date-checkbox"
                checked={isCustomDate}
                onClick={() => setIsCustomDate(!isCustomDate)}
              />
            </Box>
            {isCustomDate && (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box className={styles.datePicker}>
                  <DatePicker
                    label="Start Date"
                    value={request?.start_date}
                    onChange={(newValue) => handleChange('start_date', dayjs(newValue).utc(true))}
                  />
                  <DatePicker
                    label="End Date"
                    value={request?.end_date}
                    onChange={(newValue) => handleChange('end_date', dayjs(newValue).utc(true))}
                  />
                </Box>
              </LocalizationProvider>
            )}
          </Box>
          <Box className={styles.fieldContainer}>
            <Typography className={styles.label}>Request Name:</Typography>
            <TextField
              value={request?.name || ''}
              className={styles.field}
              onChange={(evt) => handleChange('name', evt.target.value)}
              placeholder="Enter Value"
            />
          </Box>
          <Box className={styles.fieldContainer}>
            <Typography className={styles.label}>
              Operating Unit
              <span className={styles.asterisk}>*</span>:
            </Typography>
            <SelectWithSearch
              width={300}
              value={selectedUnit ? { label: selectedUnit.name, value: selectedUnit.id } : undefined}
              label="Unit"
              handleChange={(value) => handleTierChange('f207', value ? Number(value) : undefined)}
              options={f207Tiers.map((t) => ({
                value: t.id,
                label: t.name,
              }))}
            />
          </Box>
          <Box className={styles.fieldContainer}>
            <Typography className={styles.label}>Subaccount:</Typography>
            <SelectWithSearch
              width={300}
              value={selectedSubAccount ? { label: selectedSubAccount.name, value: selectedSubAccount.id } : undefined}
              label="Sub Account"
              handleChange={(value) => handleTierChange('f910', value ? Number(value) : undefined)}
              options={f910Tiers.map((t) => ({
                value: t.id,
                label: t.name,
              }))}
            />
          </Box>
          <Box className={styles.fieldContainer}>
            <Typography className={styles.label}>Manager:</Typography>
            <TextField value={manager || ''} label="Manager" className={styles.field} disabled />
          </Box>
        </Box>
      </Box>
      <Box className={styles.sectionContainer}>
        <Box className={styles.titleContainer}>
          <Typography className={styles.title}>Releasability</Typography>
        </Box>
        <Box className={styles.section}>
          <Box className={styles.releasabilityContainer}>
            <SelectWithSearch
              value={classificationTypes
                .map((classificationType) => ({
                  label: classificationType.name,
                  value: classificationType.id,
                }))
                .find((elem) => elem.value === request?.classification_type_id)}
              label="Classification Type"
              handleChange={(value) => handleChange('classification_type_id', Number(value))}
              options={classificationTypes.map((classificationType) => ({
                value: classificationType.id,
                label: classificationType.name,
              }))}
            />
            <Typography>
              Not releasable outside the US Government IAW Section 552 (b)(1) of Title 5 of the US Code.
            </Typography>
          </Box>
        </Box>
      </Box>
      <Button
        variant="outlined"
        onClick={() => {
          setValue(1)
          handleSave()
        }}
      >
        Next
      </Button>
      {isDelete && (
        <ConfirmationModal
          onClose={handleClose}
          onConfirm={handleDelete}
          btnText={'Delete'}
          btnIcon={<DeleteIcon />}
          text={'Are you sure you want to delete this exercise request'}
        />
      )}
    </Box>
  )
}

export default GeneralSection
